import { ChangeDetectorRef, Component, inject, OnDestroy, TemplateRef, viewChild } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MapLegendDisplayComponent } from '../map-legend-display/map-legend-display.component';
import { ZsMapStateService } from '../../state/state.service';
import { GeoadminService } from '../../map-layer/geoadmin/geoadmin.service';
import { catchError, combineLatest, firstValueFrom, map, mergeMap, Observable, of, share, startWith, tap } from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { I18NService } from '../../state/i18n.service';
import { db, LocalBlobState, LocalMapInfo } from '../../db/db';
import { BlobEventType, BlobOperation } from '../../db/blob.service';
import { WmsService } from '../../map-layer/wms/wms.service';
import { WmsSourceComponent } from '../../map-layer/wms/wms-source/wms-source.component';
import { WmsLayerOptionsComponent } from '../../map-layer/wms/wms-layer-options/wms-layer-options.component';
import { GeoJSONLayerOptionsComponent } from '../../map-layer/geojson/geojson-layer-options/geojson-layer-options.component';
import { SessionService } from '../../session/session.service';
import { isEqual } from 'lodash';
import { OperationService } from '../../session/operations/operation.service';
import { OrganisationLayerSettingsComponent } from '../../map-layer/organisation-layer-settings/organisation-layer-settings.component';
import { MapLayerService } from '../../map-layer/map-layer.service';
import { BlobMetaOptionsComponent } from '../../map-layer/blob-meta-options/blob-meta-options.component';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatSliderModule } from '@angular/material/slider';
import { MatActionList, MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { AsyncPipe } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { SidebarFiltersComponent } from '../sidebar-filters/sidebar-filters.component';
import {
  GeoAdminMapLayer,
  GeoJSONMapLayer,
  IZsMapOrganizationMapLayerSettings,
  MapLayer,
  WMSMapLayer,
  WmsSource,
  ZsMapStateSource,
  zsMapStateSourceToDownloadUrl,
} from '@zskarte/types';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidebarDrawLayers } from '../sidebar-draw-layers/sidebar-draw-layers';
import { DialogBodyComponent, DialogFooterComponent, DialogHeaderComponent } from '../../ui/dialog-layout';
import { OfflineService } from '../../db/offline-service';
import { GeodiensteService } from '../../map-layer/geodienste/geodienste.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [
    MatAccordion,
    MatExpansionModule,
    MatSliderModule,
    FormsModule,
    MatActionList,
    MatRadioModule,
    AsyncPipe,
    MatProgressBarModule,
    MatIconModule,
    MatDividerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatListModule,
    SidebarFiltersComponent,
    SidebarDrawLayers,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
  ],
})
export class SidebarComponent implements OnDestroy {
  mapState = inject(ZsMapStateService);
  wmsService = inject(WmsService);
  private operationService = inject(OperationService);
  private _session = inject(SessionService);
  i18n = inject(I18NService);
  dialog = inject(MatDialog);
  offlineService = inject(OfflineService);
  private cdRef = inject(ChangeDetectorRef);
  private _mapLayerService = inject(MapLayerService);
  private _snackBar = inject(MatSnackBar);

  readonly newLayerTypeTemplate = viewChild.required<TemplateRef<HTMLElement>>('newLayerTypeTemplate');
  newLayerType?: string;
  mapProgress = 0;

  mapSources = Object.values(ZsMapStateSource)
    .map((key) => ({
      key,
      translation: this.i18n.get(key),
      selected: false,
      downloadable: this.isDownloadableMap(key),
      offlineAvailable: false,
    }))
    .sort((a, b) => a.translation.localeCompare(b.translation));
  filteredAvailableLayers$: Observable<MapLayer[]>;
  allLayers$: Observable<MapLayer[]>;
  favouriteLayers$: Observable<MapLayer[]>;
  favouriteLayerList = [
    'undefined|ch.bafu.bundesinventare-auen',
    'undefined|ch.swisstopo.swissboundaries3d-gemeinde-flaeche.fill',
    'undefined|ch.swisstopo.swisstlm3d-gewaessernetz',
    'undefined|ch.swisstopo-karto.hangneigung',
    'undefined|ch.swisstopo.swisstlm3d-strassen',
    'undefined|ch.babs.kulturgueter',
    'undefined|ch.kantone.cadastralwebmap-farbe',
    'geodienste|gefahrenkarten_v1_3_0',
    'geodienste|naturereigniskataster_v1_0_0',
    'geodienste|stromversorgungssicherheit_netzgebiete_v1_2_0',
    'geodienste|waldreservate_v2_0_0',
    'geodienste|wildruhezonen_v2_1_1',
  ];

  layerFilter = new FormControl('');
  sourceFilter = new FormControl('ALL');

  mapDownloadStates: { [key: string]: LocalBlobState } = {};
  wmsSourceLoadErrors: { [key: string]: string } = {};
  availableWmsService: WmsSource[] = [];
  geoAdminLayerError: string | undefined;
  workLocal: boolean;

  constructor() {
    const mapState = this.mapState;
    const geoAdminService = inject(GeoadminService);
    const geodiensteService = inject(GeodiensteService);
    const wmsService = this.wmsService;
    const _session = this._session;

    this.workLocal = _session.isWorkLocal();
    const geoAdminLayers$ = geoAdminService.getLayers().pipe(
      map((layers) => Object.values(layers)),
      map((layers) => layers.filter((f) => !f['parentLayerId'] && f['type'] !== 'geojson')),
      tap(() => {
        delete this.geoAdminLayerError;
      }),
      catchError((err) => {
        console.error('get geoAdminLayers failed with error', err);
        this.geoAdminLayerError = err.message;
        return of([] as GeoAdminMapLayer[]);
      }),
      share(),
    );
    const geodiensteLayers = geodiensteService.getLayers();

    function flatten<T>(arr: T[][]): T[] {
      return ([] as T[]).concat(...arr);
    }
    const wmsLayers$ = mapState.observeWmsSources$().pipe(
      mergeMap((sources) => {
        if (!sources) {
          return of([] as MapLayer[]);
        }
        this.wmsSourceLoadErrors = {};
        const promises = sources.map((wms) =>
          (wms.type === 'wmts' ? wmsService.getWMTSCapaLayers(wms) : wmsService.getWMSCapaLayers(wms)).catch((err) => {
            console.error('get WMS Layers failed for', wms, 'with error', err);
            this.wmsSourceLoadErrors[wms.url] = err.toString();
            return [] as MapLayer[];
          }),
        );
        return Promise.all(promises).then((lists) => {
          return flatten(lists);
        });
      }),
    );

    const globalMapLayers$ = mapState.observeGlobalMapLayers$();

    this.allLayers$ = combineLatest([geoAdminLayers$, geodiensteLayers, wmsLayers$, globalMapLayers$]).pipe(
      map(([geo, geodienste, wms, globalMapLayers]) => {
        return [...geo, ...geodienste, ...wms, ...globalMapLayers];
      }),
    );

    const availableLayers$: Observable<MapLayer[]> = combineLatest([
      this.allLayers$,
      mapState.observeSelectedMapLayers$(),
    ]).pipe(
      map(([source, selected]) => {
        const selectedNames = selected.map((f) => f.fullId);
        return source.filter((s) => !selectedNames.includes(s.fullId));
      }),
    );
    const filter$ = this.layerFilter.valueChanges.pipe(startWith(''));
    const selectedSource$ = this.sourceFilter.valueChanges.pipe(startWith('ALL'));

    this.filteredAvailableLayers$ = combineLatest([availableLayers$, filter$, selectedSource$]).pipe(
      map(([layers, filter, source]) => {
        layers = layers.sort((a: MapLayer, b: MapLayer) => a.label.localeCompare(b.label));
        if (source !== 'ALL') {
          if (source === '_OwnMapLayers_') {
            layers = layers.filter((f) => f.id !== undefined && f.owner);
          } else if (source === '_GlobalMapLayers_') {
            layers = layers.filter((f) => f.id !== undefined && !f.owner && !f.managed);
          } else if (source === '_ManagedMapLayers_') {
            layers = layers.filter((f) => f.id !== undefined && f.managed);
          } else if (source === '_geodienste_') {
            layers = layers.filter((f) => f.fullId.startsWith('geodienste|'));
          } else {
            const sourceFilter = source === '_GeoAdmin_' ? undefined : source;
            layers = layers.filter((f) => f.source?.url === sourceFilter);
          }
        }
        return filter === ''
          ? layers
          : layers.filter((f) => f.label.toLowerCase().includes(filter?.toLowerCase() ?? ''));
      }),
    );

    this.favouriteLayers$ = combineLatest([
      _session.observeFavoriteLayers$(),
      mapState.observeSelectedMapLayers$(),
      availableLayers$,
    ]).pipe(
      map(([favoriteLayers, selectedLayers, availableLayers]) => {
        if (favoriteLayers?.length) {
          const selectedIds = selectedLayers.map((l: MapLayer) => l.id);
          return mapState
            .getGlobalMapLayers()
            .filter((l) => l.id && favoriteLayers.includes(l.id))
            .filter((l) => !l.id || !selectedIds.includes(l.id))
            .sort((a: MapLayer, b: MapLayer) => a.label.localeCompare(b.label));
        } else {
          return availableLayers
            .filter((layer: MapLayer) => this.favouriteLayerList.includes(layer.fullId))
            .sort((a: MapLayer, b: MapLayer) => a.label.localeCompare(b.label));
        }
      }),
    );

    db.localMapInfo.toArray().then(async (downloadedMaps) => {
      this.mapDownloadStates = {};
      for (const val of downloadedMaps) {
        const mapSourceInfos = this.mapSources.find((m) => m.key === val.map);
        if (mapSourceInfos) {
          mapSourceInfos.offlineAvailable = val.offlineAvailable ?? false;
        }
        if (val.mapBlobId) {
          const meta = await db.localBlobMeta.get(val.mapBlobId);
          if (meta) {
            this.mapDownloadStates[val.map] = meta?.blobState;
            continue;
          }
        }
        this.mapDownloadStates[val.map] = 'missing';
      }
    });

    mapState
      .observeMapSource()
      .pipe(
        takeUntilDestroyed(),
        map((currentMapSource) => {
          this.mapSources.forEach((mapSource) => {
            mapSource.selected = currentMapSource === mapSource.key;
          });
        }),
      )
      .subscribe();
    this.mapSources.forEach((map) => {
      if (this.isDownloadableMap(map.key)) {
        this.offlineService.setUpdateMapCallbacks(map.key, this.updateMapCallback(map.key));
      }
    });
  }

  ngOnDestroy(): void {
    this.mapSources.forEach((map) => {
      if (this.isDownloadableMap(map.key)) {
        this.offlineService.setUpdateMapCallbacks(map.key, null);
      }
    });
  }

  switchMapSource(layer: ZsMapStateSource) {
    this.mapState.setMapSource(layer);
  }

  showLegend(item: MapLayer) {
    this.dialog.open(MapLegendDisplayComponent, {
      data: item,
    });
  }

  selectLayer(layer: MapLayer) {
    this.mapState.addMapLayer(layer);
  }

  async editLayerSettings() {
    const wmsSources = this.mapState.getGlobalWmsSources();
    const globalMapLayers = this.mapState.getGlobalMapLayers();
    const allLayers = await firstValueFrom(this.allLayers$);
    const selectedLayers = [...(await firstValueFrom(this.mapState.observeSelectedMapLayers$()))];
    const selectedSources = (await firstValueFrom(this.mapState.observeWmsSources$())) || [];
    const organization = this._session.getOrganization();
    const localMapLayerSettings = await MapLayerService.loadLocalMapLayerSettings();
    const settingsDialog = this.dialog.open(OrganisationLayerSettingsComponent, {
      data: {
        wmsSources,
        globalMapLayers,
        allLayers,
        selectedLayers,
        selectedSources,
        organization,
        localMapLayerSettings,
      },
    });
    settingsDialog.afterClosed().subscribe((result: IZsMapOrganizationMapLayerSettings) => {
      if (result) {
        //set selectedLayers on state, to make sure now saved layers have the new id information available.
        this.mapState.updateDisplayState((draft) => {
          draft.layers = selectedLayers;
        });
        //persist organisation settings
        this._session.saveOrganizationMapLayerSettings(result);
      }
    });
  }

  async editWmsSources() {
    const sources = (await firstValueFrom(this.mapState.observeWmsSources$())) || [];
    const sourceDialog = this.dialog.open(WmsSourceComponent, {
      data: sources,
    });
    sourceDialog.afterClosed().subscribe(async (changedSources: WmsSource[]) => {
      if (changedSources) {
        const ownSources = changedSources.filter((s) => s.owner);
        const changedOwnSources = ownSources.filter((ownSource) => {
          let source = sources.find((s) => s.owner && s.documentId === ownSource.documentId);
          if (!source) {
            source = sources.find((s) => s.owner && s.url === ownSource.url);
          }
          return !source || !isEqual(ownSource, source);
        });
        const organizationId = this._session.getOrganization()?.documentId;
        if (organizationId) {
          for (const changedOwnSource of changedOwnSources) {
            const updatedSource = await this.wmsService.saveGlobalWMSSource(changedOwnSource, organizationId);
            if (updatedSource) {
              const index = changedSources.indexOf(changedOwnSource);
              if (index !== -1) {
                changedSources[index] = updatedSource;
              }
            }
          }
        }
        this.mapState.setWmsSources(changedSources);
        this.mapState.reloadAllMapLayers();
      }
    });
  }

  showWmsLayerOptions(item: MapLayer, index: number) {
    const optionsDialog = this.dialog.open(WmsLayerOptionsComponent, {
      data: item as WMSMapLayer,
    });
    optionsDialog.afterClosed().subscribe((layer: WMSMapLayer) => {
      if (layer) {
        if (index === -1) {
          this.mapState.addMapLayer(layer);
        } else {
          this.mapState.replaceMapLayer(layer, index);
        }
      }
    });
  }

  showGeoJSONLayerOptions(item: MapLayer, index: number) {
    const optionsDialog = this.dialog.open(GeoJSONLayerOptionsComponent, {
      data: item,
    });
    optionsDialog.afterClosed().subscribe((layer: GeoJSONMapLayer) => {
      if (layer) {
        if (index === -1) {
          this.mapState.addMapLayer(layer);
        } else {
          this.mapState.replaceMapLayer(layer, index);
        }
      }
    });
  }

  showLocalInfo(item: MapLayer, index: number) {
    const localDialog = this.dialog.open(BlobMetaOptionsComponent, {
      data: { mapLayer: item },
      disableClose: true,
    });
    localDialog.afterClosed().subscribe(async (layer: MapLayer | undefined) => {
      if (layer) {
        await this._mapLayerService.saveLocalMapLayer(layer, false);
        this.mapState.replaceMapLayer(layer, index);
      }
    });
  }

  async showLocalInfoMap(map: ZsMapStateSource, index: number) {
    const localMapInfo = await db.localMapInfo.get(map);
    const localDialog = this.dialog.open(BlobMetaOptionsComponent, {
      data: { localMap: localMapInfo || { map } },
      disableClose: true,
    });
    localDialog.afterClosed().subscribe(async (localMapInfo: LocalMapInfo | undefined) => {
      if (localMapInfo) {
        await db.localMapInfo.put(localMapInfo);
        this.mapSources[index].offlineAvailable = localMapInfo.offlineAvailable ?? false;
        this.reloadSourceIfLocal();
      }
    });
  }

  addNewLayer() {
    const optionsDialog = this.dialog.open(this.newLayerTypeTemplate());
    optionsDialog.afterClosed().subscribe((type: string) => {
      if (type === 'wms_custom') {
        this.showWmsLayerOptions({ type, serverLayerName: '' } as WMSMapLayer, -1);
      } else if (type === 'geojson' || type === 'shape' || type === 'csv') {
        this.showGeoJSONLayerOptions({ type, opacity: 1.0, serverLayerName: null } as GeoJSONMapLayer, -1);
      }
    });
  }

  async persistLayers() {
    const operationId = this._session.getOperationId();
    if (operationId) {
      const mapSource = await firstValueFrom(this.mapState.observeMapSource());
      const selectedLayers = await firstValueFrom(this.mapState.observeSelectedMapLayers$());

      //only save difference to globalMapLayer informations
      //if this is saved like that it's clean but complicated to rehydrate the required informations on loading...
      //const allLayers = await firstValueFrom(this.allLayers$);
      //const layerConfigs = selectedLayers.map((layer) => MapLayerService.extractMapLayerDiffs(layer, allLayers));
      const layerConfigs = selectedLayers;
      const mapLayers = { baseLayer: mapSource, layerConfigs };
      const operation = this._session.getOperation();
      if (operation) {
        operation.mapLayers = mapLayers;
      }
      await this.operationService.updateMapLayers(operationId, mapLayers);

      this._snackBar.open(this.i18n.get('toastPersistLayers'), 'OK', {
        duration: 2000,
      });
    }
  }

  // skipcq: JS-0105
  isDownloadableMap(map: ZsMapStateSource) {
    return map in zsMapStateSourceToDownloadUrl;
  }

  // skipcq: JS-0105
  isSearchable(item: MapLayer) {
    return (
      (item.type === 'geojson' || item.type === 'shape' || item.type === 'csv') &&
      ((item as GeoJSONMapLayer)?.searchable || false)
    );
  }

  async uploadMap(event: Event, map: ZsMapStateSource) {
    await this.offlineService.uploadMap(event, map);
    this.mapDownloadStates[map] = 'loading';
  }

  async removeLocalMap(map: ZsMapStateSource): Promise<void> {
    await this.offlineService.removeLocalMap(map);
    this.mapDownloadStates[map] = 'missing';
    const mapSource = this.mapSources.find((m) => m.key === map);
    if (mapSource) {
      mapSource.offlineAvailable = false;
    }
    this.mapProgress = 0;
    this.cdRef.detectChanges();
    this.reloadSourceIfLocal();
  }

  private updateMapCallback(map: ZsMapStateSource) {
    // skipcq: JS-0116
    return async (eventType: BlobEventType, infos: BlobOperation) => {
      this.mapDownloadStates[map] = infos.localBlobMeta.blobState;
      this.mapProgress = infos.mapProgress;
      this.cdRef.detectChanges();
      if (eventType !== 'mapProgress') {
        const mapSource = this.mapSources.find((m) => m.key === map);
        if (eventType === 'done') {
          this.reloadSourceIfLocal();
          if (mapSource) {
            mapSource.offlineAvailable = true;
          }
        } else if (mapSource) {
          mapSource.offlineAvailable = false;
        }
      }
    };
  }

  private reloadSourceIfLocal() {
    if (this.mapSources.find((m) => m.selected && m.key === ZsMapStateSource.LOCAL)) {
      //it's the active one reload it to use new location
      this.mapState.setMapSource(ZsMapStateSource.OPEN_STREET_MAP);
      this.mapState.setMapSource(ZsMapStateSource.LOCAL);
    }
  }
}
